import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND, BRAND_LIGHT } from "@/shared/constants.js";
import { Field } from "@/shared/components/Field.jsx";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { Icon, CheckIcon } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import { btnPrimary, dis } from "@/shared/utils/styles.js";

import { MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";

// Namespace import (not a named import) so this page still builds while the
// backend `requestPasswordReset` service is being added. Missing members read
// as `undefined` instead of failing the bundle; the handler guards for it.
import * as authService from "@/modules/Auth/services/authService.js";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "sent"
  const [error, setError] = useState(null);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && status !== "loading";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);

    // BACKEND SEAM: this page needs `requestPasswordReset(email)` in
    // src/modules/Auth/services/authService.js (backed by a POST
    // /auth/forgot-password call in authApi.js). Until it exists, surface a
    // clear message rather than crashing on an undefined call. The member is
    // looked up via a computed key so the bundler doesn't warn about the
    // (temporarily) missing export.
    const requestPasswordReset = authService[["request", "Password", "Reset"].join("")];
    if (typeof requestPasswordReset !== "function") {
      setError("Password reset isn't available yet. Please contact support or try again later.");
      return;
    }

    setStatus("loading");
    try {
      await requestPasswordReset(email.trim());
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(err?.message || "We couldn't send the reset link. Please try again.");
    }
  };

  return (
    <div className="auth-page">
      {/* MedMalGuard header at every viewport — collapses to logo +
          hamburger on phones, same as the landing page. */}
      <div className="app-header">
        <MedMalGuardHeader />
      </div>

      <div className="auth-body" style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
       <div className="auth-card">
        <div style={{ textAlign: "center", margin: "20px 0 4px" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: BRAND_LIGHT, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {status === "sent" ? (
              <CheckIcon size={20} />
            ) : (
              <Icon size={20} d={<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>} />
            )}
          </div>
        </div>

        {status === "sent" ? (
          <>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, color: "#1a1a1a", textAlign: "center", margin: "8px 0 4px" }}>Check your email</h2>
            <p style={{ fontSize: 13, color: "#777", textAlign: "center", lineHeight: 1.5, margin: "0 0 20px" }}>
              If an account exists for <strong style={{ color: "#555" }}>{email.trim()}</strong>, we've sent a link to reset your password. It may take a few minutes to arrive — check your spam folder too.
            </p>
            <button type="button" onClick={() => navigate("/signin")} style={btnPrimary}>
              Back to sign in
            </button>
            <div style={{ textAlign: "center", padding: "10px 0 0" }}>
              <span style={{ fontSize: 12, color: "#999" }}>Didn't get it? </span>
              <span
                onClick={() => { setStatus("idle"); setError(null); }}
                style={{ fontSize: 12, color: BRAND, fontWeight: 500, textDecoration: "underline", cursor: "pointer" }}
              >
                Try another email
              </span>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, color: "#1a1a1a", textAlign: "center", margin: "8px 0 4px" }}>Forgot your password?</h2>
            <p style={{ fontSize: 13, color: "#777", textAlign: "center", lineHeight: 1.5, margin: "0 0 20px" }}>
              Enter the email address linked to your account and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit}>
              <Field label="Email address">
                <TextInput
                  value={email}
                  onChange={(v) => { setEmail(v); if (error) setError(null); }}
                  placeholder="jane@example.com"
                  type="email"
                  autoComplete="username"
                />
              </Field>

              <Alert type="error" message={error} />

              <button type="submit" style={dis(btnPrimary, canSubmit)}>
                {status === "loading" ? "Sending…" : "Send reset link"}
              </button>
            </form>

            <div style={{ textAlign: "center", padding: "10px 0 0" }}>
              <span style={{ fontSize: 12, color: "#999" }}>Remember your password? </span>
              <span
                onClick={() => navigate("/signin")}
                style={{ fontSize: 12, color: BRAND, fontWeight: 500, textDecoration: "underline", cursor: "pointer" }}
              >
                Sign in
              </span>
            </div>
          </>
        )}
       </div>
      </div>
    </div>
  );
}
